import toastMessage from '@/components/Toast';
import {
  CommoditiesForwardCurvesResponse,
  CommoditiesVolatilityCurvesResponse,
  DownloadHistoricalForwardCurveReportPayloadTypes,
  DownloadInterestCostCalcReportPayloadTypes,
  DownloadIrAnalysisPayloadTypes,
  DownloadIrForwardCurveReportPayloadTypes,
  ForeignExchangeAnalysisResponse,
  ForwardCurveResponse,
  HistoricalForwardCurvesResponse,
  InterestCostCalculationsResponse,
  InterestRateAnalysisCurvesResponse,
  MarketOverviewCurrencyRateResponse,
  MarketOverviewInterestRateResponse,
} from '@/store/marketData/types';
import { getThunkError } from '@/store/utility/thunkErrorUtils';
import apiInstance from '@api/index';
import { createAsyncThunk } from '@reduxjs/toolkit';

import { toastSuccessOnce } from '../components/IrMarketDataCard/components/utils/toastOnce';

import {
  AsyncThunkConfig,
  CheckCommodityAvailabilityPayload,
  CheckCommodityAvailabilityResult,
  CommodityForwardCurvesDataPayload,
  CommodityIndex,
  CommodityVolatilityCurvesDataPayload,
  Currency,
  CurrencyPairTypes,
  DownloadIrForwardCurveReportParams,
  FxAnalysisPayload,
  FxForwardCurveMissingPair,
  FxForwardCurvePairData,
  FxForwardCurvesPayload,
  IrIndex,
  ReportResult,
  RiskFreeRateIndicesResponse,
  SecuritiesTypes,
  ValidateFxCurrencyParams,
  ValidateFxCurrencyResult,
  ValidateIrIndexParams,
  ValidateIrIndexResult,
} from './types';

export const fetchIrIndexList = createAsyncThunk<
  IrIndex[],
  void,
  AsyncThunkConfig
>('marketData/fetchIrIndexList', async (_, { rejectWithValue }) => {
  try {
    const response = await apiInstance.marketData.fetchIrIndexList();
    return response.data.data as IrIndex[];
  } catch (error: any) {
    return rejectWithValue(
      error.message || 'An unknown error occurred in fetchIrIndexList request',
    );
  }
});

export const fetchCurrencyPair = createAsyncThunk<
  CurrencyPairTypes[],
  void,
  AsyncThunkConfig
>('marketData/fetchCurrencyPair', async (_, { rejectWithValue }) => {
  try {
    const response = await apiInstance.marketData.fetchCurrencyPair();
    return response.data.data as CurrencyPairTypes[];
  } catch (error: any) {
    return rejectWithValue(
      error.message || 'An unknown error occurred in fetchCurrencyPair request',
    );
  }
});

export const fetchSecurities = createAsyncThunk<
  SecuritiesTypes[],
  void,
  AsyncThunkConfig
>('marketData/fetchSecurities', async (_, { rejectWithValue }) => {
  try {
    const response = await apiInstance.marketData.fetchSecurities();
    return response.data.data as SecuritiesTypes[];
  } catch (error: any) {
    return rejectWithValue(
      error.message || 'An unknown error occurred in fetchSecurities request',
    );
  }
});

export const downloadIrForwardCurveReport = createAsyncThunk<
  ReportResult,
  DownloadIrForwardCurveReportParams,
  AsyncThunkConfig
>(
  'marketData/downloadIrForwardCurveReport',
  async (payload, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.marketData.downloadIrForwardCurveReport(payload);
      const data = response.data.data;
      return {
        file_path: data.file_path,
        file_name: data.file_name,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.message ||
          'An unknown error occurred in downloadIrForwardCurveReport',
      );
    }
  },
);

export const fetchMarketDataCurrencies = createAsyncThunk<
  Currency[],
  void,
  AsyncThunkConfig
>('marketData/fetchMarketDataCurrencies', async (_, { rejectWithValue }) => {
  try {
    const response = await apiInstance.currency.getMarketDataCurrencies();
    return response.data.data as Currency[];
  } catch (error: any) {
    return rejectWithValue(
      error.message || 'An unknown error occurred in fetchMarketDataCurrencies',
    );
  }
});

export const fetchCurrencyPairForm = createAsyncThunk<
  [],
  void,
  AsyncThunkConfig
>('marketData/fetchCurrencyPairForm', async (_, { rejectWithValue }) => {
  try {
    const response = await apiInstance.marketData.getCurrencyPairForm();
    return response.data.data; //as CurrencyPairForm;
  } catch (error: any) {
    return rejectWithValue(
      error.message || 'An unknown error occurred in fetchCurrencyPairForm',
    );
  }
});

export const fetchCommodityIndices = createAsyncThunk<
  CommodityIndex[],
  void,
  AsyncThunkConfig
>('marketData/fetchCommodityIndices', async (_, { rejectWithValue }) => {
  try {
    const response = await apiInstance.marketData.getCommoditiesIndices();
    return response.data.data as CommodityIndex[];
  } catch (error: any) {
    return rejectWithValue(
      error.message || 'An unknown error occurred in fetchCommodityIndices',
    );
  }
});

export const downloadCommodityForwardCurveReport = createAsyncThunk<
  ReportResult,
  { run_date: string; indices: number[]; frequency: string },
  AsyncThunkConfig
>(
  'marketData/fetchCommodityForwardCurveReport',
  async (payload, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.marketData.downloadCommodityForwardCurveReport(
          payload,
        );
      const data = response.data.data;
      return {
        file_path: data.file_path,
        file_name: data.file_name,
        file_s3_url: data.file_s3_url,
      };
    } catch (error: any) {
      toastMessage.error(
        error.response?.data?.error_msg ||
          'Failed to download commodity forward curve report',
      );
      return rejectWithValue(
        error.response?.data ||
          'Failed to download commodity forward curve report',
      );
    }
  },
);

export const reportFxForwardCurves = createAsyncThunk<
  ReportResult,
  FxForwardCurvesPayload,
  AsyncThunkConfig
>('marketData/reportFxForwardCurves', async (payload, { rejectWithValue }) => {
  try {
    const response =
      await apiInstance.marketData.reportFxForwardCurves(payload);
    const data = response.data.data;
    return {
      file_path: data.file_path,
      file_name: data.file_name,
      file_s3_url: data.file_s3_url,
    };
  } catch (error: any) {
    toastMessage.error(
      error.response?.data?.error_msg ||
        'An unknown error occurred in reportFxForwardCurves request',
    );
    return rejectWithValue(error.response?.data ?? 'Failed to fetch report');
  }
});

export const checkCommodityAvailability = createAsyncThunk<
  CheckCommodityAvailabilityResult,
  CheckCommodityAvailabilityPayload,
  AsyncThunkConfig
>(
  'marketData/checkCommodityAvailability',
  async ({ run_date, indices }, { rejectWithValue }) => {
    try {
      const response = await apiInstance.marketData.checkCommodityAvailability({
        run_date,
        indices,
      });
      return response.data as CheckCommodityAvailabilityResult;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || 'Failed to check commodity availability',
      );
    }
  },
);

export const validateFxCurrency = createAsyncThunk<
  ValidateFxCurrencyResult,
  ValidateFxCurrencyParams,
  AsyncThunkConfig
>('marketData/validateFxCurrency', async (params, { rejectWithValue }) => {
  try {
    const response = await apiInstance.marketData.validateFxCurrency(params);
    return response.data as ValidateFxCurrencyResult;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data || 'Failed to validate FX currency',
    );
  }
});

export const validateIrIndex = createAsyncThunk<
  ValidateIrIndexResult,
  ValidateIrIndexParams,
  AsyncThunkConfig
>('marketData/validateIrIndex', async (params, { rejectWithValue }) => {
  try {
    const response = await apiInstance.marketData.validateIrIndex(params);
    const data = response.data as ValidateIrIndexResult;

    if (data?.data && Object.keys(data.data).length > 0) {
      toastSuccessOnce('Data available for the selected date');
    }

    return data;
  } catch (error: any) {
    toastMessage.error('Failed to validate IR index');
    return rejectWithValue(
      error.response?.data || 'Failed to validate IR index',
    );
  }
});

export const getForwardCurvesData = createAsyncThunk<
  ForwardCurveResponse,
  DownloadIrForwardCurveReportPayloadTypes,
  AsyncThunkConfig
>('marketData/getForwardCurvesData', async (params, { rejectWithValue }) => {
  try {
    const response = await apiInstance.marketData.getForwardCurvesData(params);
    const data = response.data;
    return data;
  } catch (error: any) {
    const message = getThunkError(error);
    toastMessage.error(message || 'There is no data');
    return rejectWithValue(error.response?.data);
  }
});

export const getHistoricalForwardCurvesData = createAsyncThunk<
  HistoricalForwardCurvesResponse,
  DownloadHistoricalForwardCurveReportPayloadTypes,
  AsyncThunkConfig
>(
  'marketData/getHistoricalForwardCurvesData',
  async (params, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.marketData.getHistoricalForwardCurvesData(params);
      const data = response.data;
      return data;
    } catch (error: any) {
      const message = getThunkError(error);
      toastMessage.error(message || 'There is no data');
      return rejectWithValue(error.response?.data);
    }
  },
);

export const getInterestRateAnalysisCurvesData = createAsyncThunk<
  InterestRateAnalysisCurvesResponse,
  DownloadIrAnalysisPayloadTypes,
  AsyncThunkConfig
>(
  'marketData/getInterestRateAnalysisCurvesData',
  async (params, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.marketData.getInterestRateAnalysisData(params);
      const data = response.data;
      return data;
    } catch (error: any) {
      const message = getThunkError(error);
      toastMessage.error(message || 'There is no data');
      return rejectWithValue(error.response?.data);
    }
  },
);

export const getInterestCostCalculationsData = createAsyncThunk<
  InterestCostCalculationsResponse,
  DownloadInterestCostCalcReportPayloadTypes,
  AsyncThunkConfig
>(
  'marketData/getInterestCostCalculationsData',
  async (params, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.marketData.getInterestCostCalculationsData(params);
      const data = response.data;
      return data;
    } catch (error: any) {
      const message = getThunkError(error);
      toastMessage.error(message || 'There is no data');
      return rejectWithValue(error.response?.data);
    }
  },
);

export const getForeignExchangeAnalysisData = createAsyncThunk<
  ForeignExchangeAnalysisResponse,
  FxAnalysisPayload,
  AsyncThunkConfig
>(
  'marketData/getForeignExchangeAnalysisData',
  async (params, { rejectWithValue }) => {
    try {
      const response = await apiInstance.marketData.getFxAnalysisData(params);
      const data = response.data;
      return data;
    } catch (error: any) {
      const message = getThunkError(error);
      toastMessage.error(message || 'There is no data');
      return rejectWithValue(error.response?.data);
    }
  },
);

export const getCommoditiesForwardCurvesData = createAsyncThunk<
  CommoditiesForwardCurvesResponse,
  CommodityForwardCurvesDataPayload,
  AsyncThunkConfig
>(
  'marketData/getCommoditiesForwardCurvesData',
  async (params, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.marketData.getCommoditiesFCData(params);
      const data = response.data;
      return data;
    } catch (error: any) {
      const message = getThunkError(error);
      toastMessage.error(message || 'There is no data');
      return rejectWithValue(error.response?.data);
    }
  },
);

export const getCommoditiesVolatilityCurvesData = createAsyncThunk<
  CommoditiesVolatilityCurvesResponse,
  CommodityVolatilityCurvesDataPayload,
  AsyncThunkConfig
>(
  'marketData/getCommoditiesVolatilityCurvesData',
  async (params, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.marketData.getCommoditiesVolCurvesData(params);
      const data = response.data;
      return data;
    } catch (error: any) {
      const message = getThunkError(error);
      toastMessage.error(message || 'There is no data');
      return rejectWithValue(error.response?.data);
    }
  },
);

export const getMarketOverviewCurrencyRatesData = createAsyncThunk<
  MarketOverviewCurrencyRateResponse,
  void,
  AsyncThunkConfig
>(
  '/marketData/getMarketOverviewCurrencyRatesData',
  async (_, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.marketData.getMarketOverviewCurrencyRates();
      const data = response.data;
      return data;
    } catch (error: any) {
      const message = getThunkError(error);
      toastMessage.error(message || 'There is no data');
      return rejectWithValue(error.response?.data);
    }
  },
);

export const getFxForwardCurvesData = createAsyncThunk<
  { run_date: string; pairs: FxForwardCurvePairData[] },
  FxForwardCurvesPayload,
  AsyncThunkConfig
>('marketData/getFxForwardCurvesData', async (payload, { rejectWithValue }) => {
  try {
    const response =
      await apiInstance.marketData.getFxForwardCurvesData(payload);
    const data = response.data.data as {
      run_date: string;
      pairs: FxForwardCurvePairData[];
      missing_pairs?: FxForwardCurveMissingPair[];
    };
    if (data.missing_pairs?.length) {
      const details = data.missing_pairs
        .map((p) => `${p.label}: ${p.reason}`)
        .join('\n');
      toastMessage.warning(`No data available for some pairs:\n${details}`);
    }
    return data;
  } catch (error: any) {
    const message = getThunkError(error);
    toastMessage.error(
      error.response?.data?.error_msg ||
        message ||
        'Failed to fetch FX forward curves',
    );
    return rejectWithValue(
      error.response?.data ?? 'Failed to fetch FX forward curves',
    );
  }
});

export const getMarketOverviewInterestRatesData = createAsyncThunk<
  MarketOverviewInterestRateResponse,
  void,
  AsyncThunkConfig
>(
  '/marketData/getMarketOverviewInterestRatesData',
  async (_, { rejectWithValue }) => {
    try {
      const response =
        await apiInstance.marketData.getMarketOverviewInterestRates();
      const data = response.data;
      return data;
    } catch (error: any) {
      const message = getThunkError(error);
      toastMessage.error(message || 'There is no data');
      return rejectWithValue(error.response?.data);
    }
  },
);

export const getRiskFreeRateIndices = createAsyncThunk<
  RiskFreeRateIndicesResponse,
  void,
  AsyncThunkConfig
>('marketData/getRiskFreeRateIndices', async (_, { rejectWithValue }) => {
  try {
    const response = await apiInstance.marketData.getRiskFreeRateIndices();
    const data = response.data as RiskFreeRateIndicesResponse;
    return data;
  } catch (error: any) {
    const message = getThunkError(error);
    toastMessage.error(message || 'Failed to fetch risk-free rate indices');
    return rejectWithValue(
      error.response?.data || 'Failed to fetch risk-free rate indices',
    );
  }
});
